import React from 'react';

import { StatBadge } from '../StatBadge/StatBadge';
import { Reveal } from '../Reveal/Reveal';
import { Skeleton } from '../Skeleton/Skeleton';
import type { Kpi, KpiStat } from '../../mock-data/types';
import {
  Tile,
  Head,
  HeadText,
  TileTitle,
  TileSubtitle,
  Stats,
  Stat,
  StatDivider,
  StatPeriod,
  StatBody,
  StatValue,
} from './KpiTile.styles';

export { KpiGrid } from './KpiTile.styles';

export type KpiTileProps = {
  kpi: Kpi;
  /** While true, the today/month figures + delta badges show placeholders (loading). */
  loading?: boolean;
};

/**
 * A figure with no real value renders a marker instead of a number (the same
 * convention as Home / Stats / the acquisition Performance grid): `?` not wired to an
 * API field, `N/A` the request is failing, `–` the value is undefined this period (a
 * ratio dividing by zero, e.g. a per-FTD figure with zero FTDs). Each also drops the
 * delta badge. `null` means a normal numeric figure.
 */
const stateGlyph = (stat: KpiStat): string | null =>
  stat.missing ? '?' : stat.error ? 'N/A' : stat.indeterminate ? '–' : null;

/**
 * One KPI tile — a titled card (title + subtitle) over an inner panel that splits
 * the metric into a Today / This-month pair, each with its value and a delta
 * badge, divided by a faint rule. Shared by the Running-Campaign performance
 * section. The title/subtitle/period labels are static chrome; the figures +
 * delta badges load in (placeholders live inside `StatValue` so they inherit its font).
 * A marker figure (`?` / `N/A` / `–`) shows the marker in place of the number and drops
 * its delta badge.
 */
export const KpiTile = ({ kpi, loading = false }: KpiTileProps): React.ReactElement => {
  const todayGlyph = stateGlyph(kpi.today);
  const monthGlyph = stateGlyph(kpi.month);
  return (
    <Tile>
      <Head>
        <HeadText>
          <TileTitle text={kpi.title} tag="span" />
          <TileSubtitle text={kpi.subtitle} tag="span" />
        </HeadText>
      </Head>
      <Stats>
        <Stat $align="start">
          <StatPeriod text="Today" tag="span" />
          <StatBody>
            <StatValue>
              {todayGlyph ? (
                todayGlyph
              ) : (
                <Reveal loading={loading} placeholder={<Skeleton width={`${Math.max(2, kpi.today.value.length)}ch`} height="0.8em" radius={6} />}>
                  {kpi.today.value}
                </Reveal>
              )}
            </StatValue>
            {todayGlyph ? null : (
              <Reveal loading={loading} placeholder={<Skeleton width={56} height={18} radius={7} />}>
                <StatBadge change={kpi.today.change} changeType={kpi.today.changeType} vs="vs yesterday" flat={kpi.today.flat} />
              </Reveal>
            )}
          </StatBody>
        </Stat>
        <StatDivider />
        <Stat $align="end">
          <StatPeriod text="This month" tag="span" />
          <StatBody>
            <StatValue>
              {monthGlyph ? (
                monthGlyph
              ) : (
                <Reveal loading={loading} placeholder={<Skeleton width={`${Math.max(2, kpi.month.value.length)}ch`} height="0.8em" radius={6} />}>
                  {kpi.month.value}
                </Reveal>
              )}
            </StatValue>
            {monthGlyph ? null : (
              <Reveal loading={loading} placeholder={<Skeleton width={56} height={18} radius={7} />}>
                <StatBadge change={kpi.month.change} changeType={kpi.month.changeType} vs="vs last month" flat={kpi.month.flat} />
              </Reveal>
            )}
          </StatBody>
        </Stat>
      </Stats>
    </Tile>
  );
};

export default KpiTile;
